"use client";
import { ArtOrderOffer } from "@/api/art-order/types";
import { PhaseState } from "@/app/(components)/Basic/PhaseBar/constants";
import { ProgressModal } from "@/app/(components)/Basic/ProgressModal";
import { useProgressContext } from "@/app/(components)/Basic/ProgressModal/context/context";
import useWithCorrectNetwork from "@/shared/hooks/useWithCorrectNetwork";
import { useEffect } from "react";
import useCancelArtOrder from "../hooks/useCancelArtOrder";
import {
  CancelArtOrderPhase,
  CANCEL_ART_ORDER_INITIAL_STATE,
} from "./constants";
import { useQueryClient } from "@tanstack/react-query";
import { ART_ORDER_DEFAULT_PAGE } from "../../../constants";

export interface Props {
  artOrderOffer: ArtOrderOffer;
  onSuccess: () => void;
}

export default function CancelArtOrderModal({
  artOrderOffer,
  onSuccess,
}: Props) {
  const { isOpen, phaseState, error, onClose, setPhaseState, handleError } =
    useProgressContext();

  const queryClient = useQueryClient();

  const { cancelArtOrder } = useCancelArtOrder(artOrderOffer, {
    onSuccess: () => {
      setPhaseState((state) => ({
        ...state,
        [CancelArtOrderPhase.CANCELING]: PhaseState.SUCCESS,
      }));

      onSuccess();

      queryClient.invalidateQueries({
        queryKey: ["all-active-art-orders", ART_ORDER_DEFAULT_PAGE],
      });
      queryClient.invalidateQueries({
        queryKey: ["all-active-art-orders-count", ART_ORDER_DEFAULT_PAGE],
      });
    },
    onError: (error: any) => {
      handleError(CancelArtOrderPhase.CANCELING, error);
    },
  });

  useEffect(() => {
    setPhaseState(CANCEL_ART_ORDER_INITIAL_STATE);
  }, [setPhaseState]);

  const checkedCancelArtOrder = useWithCorrectNetwork(cancelArtOrder);

  useEffect(() => {
    if (phaseState[CancelArtOrderPhase.CANCELING] !== PhaseState.PENDING) {
      return;
    }

    checkedCancelArtOrder();
  }, [phaseState, checkedCancelArtOrder]);

  return (
    <ProgressModal
      text="Your order is being canceled."
      isOpen={!!isOpen}
      handleClose={onClose}
      phaseState={phaseState}
      phases={[
        {
          id: CancelArtOrderPhase.CANCELING,
          description: "Canceling order",
        },
      ]}
      error={error}
    />
  );
}
